import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { FaQrcode, FaArrowLeft, FaCheckCircle, FaTimesCircle, FaCamera } from 'react-icons/fa';
import { getToken } from '../utils/api';

const API_CHECKIN = 'https://dev.sliitleo.org/Backend/checkin_scan.php';
const SCANNER_ELEMENT_ID = 'checkin-scanner-viewport';

async function postJson(url, params) {
  const token = getToken();
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: params.toString(),
  });
  const raw = await res.text();
  let json = null;
  try { json = JSON.parse(raw); } catch { /* non-JSON response */ }
  return { ok: res.ok, status: res.status, json };
}

// One scanner instance per open tab/device -- each admin's own phone runs
// its own independent scanner, so "multi-user" concurrency is just "every
// admin has this page open on their own device"; the actual race-safety
// (two people scanning the same QR at once) is handled server-side in
// checkin_scan.php's conditional UPDATE.
export default function CheckInScanner() {
  const user = JSON.parse(localStorage.getItem('user')) || {
    SLIITID: '', name: 'Unknown User', role: 'Member', image: '/user.png',
  };

  const scannerRef = useRef(null);
  const isScanningRef = useRef(false);
  const [cameraError, setCameraError] = useState('');
  const [status, setStatus] = useState('idle'); // idle | scanning | looking-up | result
  const [result, setResult] = useState(null); // { found, alreadyChecked, data, error }
  const [confirming, setConfirming] = useState(false);

  const handleDecoded = useCallback(async (decodedText) => {
    if (!isScanningRef.current) return; // ignore while a result is being shown
    isScanningRef.current = false;
    if (scannerRef.current) {
      try { await scannerRef.current.pause(true); } catch { /* already paused/stopped */ }
    }

    setStatus('looking-up');
    // QR payload is just the raw checkin_token string.
    const token = String(decodedText || '').trim();
    try {
      const url = `${API_CHECKIN}?action=lookup&token=${encodeURIComponent(token)}`;
      const authToken = getToken();
      const res = await fetch(url, {
        cache: 'no-store',
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
      });
      const json = await res.json().catch(() => null);
      if (!json?.success) {
        setResult({ found: false, error: 'Could not read this code.' });
      } else if (!json.found) {
        setResult({ found: false, error: 'No registration matches this QR code.' });
      } else {
        setResult({ found: true, token, data: json.data });
      }
    } catch (e) {
      setResult({ found: false, error: 'Network error looking up this code.' });
    }
    setStatus('result');
  }, []);

  const startScanner = useCallback(async () => {
    setCameraError('');
    setResult(null);
    setStatus('scanning');
    isScanningRef.current = true;

    if (!scannerRef.current) {
      scannerRef.current = new Html5Qrcode(SCANNER_ELEMENT_ID);
    }
    try {
      await scannerRef.current.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        handleDecoded,
        () => { /* per-frame decode miss -- expected constantly, ignore */ }
      );
    } catch (e) {
      setCameraError(
        'Could not access the camera. Make sure you allowed camera permission, ' +
        'and that no other app/tab is using it.'
      );
      setStatus('idle');
      isScanningRef.current = false;
    }
  }, [handleDecoded]);

  useEffect(() => {
    startScanner();
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {}).finally(() => {
          try { scannerRef.current.clear(); } catch { /* noop */ }
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scanNext = async () => {
    setResult(null);
    setStatus('scanning');
    isScanningRef.current = true;
    if (scannerRef.current) {
      try { await scannerRef.current.resume(); }
      catch { await startScanner(); }
    } else {
      await startScanner();
    }
  };

  const handleConfirmCheckIn = async () => {
    if (!result?.found || confirming) return;
    setConfirming(true);
    try {
      const params = new URLSearchParams();
      params.set('action', 'checkin');
      params.set('token', result.token);
      params.set('checkedBy', String(user.SLIITID || ''));
      params.set('checkedByName', String(user.name || 'Admin'));
      const { json } = await postJson(API_CHECKIN, params);
      if (!json?.success) {
        const msg = json?.message === 'not_verified'
          ? 'This registration is not verified yet -- cannot check in.'
          : json?.message === 'not_participating'
            ? 'Registered as NOT participating -- check them in manually from the Installation list.'
            : 'Check-in failed. Try again.';
        setResult(prev => ({ ...prev, error: msg }));
      } else {
        setResult(prev => ({
          ...prev,
          data: json.data,
          alreadyChecked: !!json.alreadyChecked,
          justCheckedIn: json.checkedInNow === true,
        }));
      }
    } catch {
      setResult(prev => ({ ...prev, error: 'Network error. Try again.' }));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <>
      <Sidebar />
      <Topbar user={user} />
      <main className="lg:ml-64 mt-14 px-4 sm:px-6 py-6 sm:py-8" style={{ maxWidth: 560, margin: '0 auto' }}>
        <div className="page-head">
          <div>
            <h1><FaQrcode style={{ marginRight: 10, color: 'var(--accent)' }} />Check-in Scanner</h1>
            <p className="page-desc">Scan a registrant's QR code, then confirm check-in.</p>
          </div>
          <Link to="/installation" className="btn btn-ghost btn-sm">
            <FaArrowLeft /> Back to Installation
          </Link>
        </div>

        <div className="panel" style={{ padding: 'var(--sp-4)' }}>
          <div
            id={SCANNER_ELEMENT_ID}
            style={{
              width: '100%',
              borderRadius: 'var(--radius-md)',
              overflow: 'hidden',
              background: '#000',
              minHeight: 280,
            }}
          />

          {cameraError && (
            <div className="error-banner" style={{ marginTop: 'var(--sp-4)' }}>
              <FaCamera style={{ marginRight: 8 }} />
              {cameraError}
              <button className="btn btn-primary btn-sm" style={{ marginLeft: 12 }} onClick={startScanner}>
                Retry Camera
              </button>
            </div>
          )}

          {status === 'looking-up' && (
            <div style={{ textAlign: 'center', padding: 'var(--sp-5) 0', color: 'var(--text-2)' }}>
              <span className="spinner" style={{ marginRight: 8 }} />
              Looking up registration…
            </div>
          )}

          {status === 'result' && result && (
            <div style={{ marginTop: 'var(--sp-4)' }}>
              {!result.found ? (
                <div className="error-banner">
                  <FaTimesCircle style={{ marginRight: 8 }} />
                  {result.error || 'No registration found for this code.'}
                </div>
              ) : (
                <div className="panel" style={{ padding: 'var(--sp-4)', background: 'var(--surface-2)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div>
                      <div className="proj-name" style={{ fontSize: 18 }}>{result.data.name || 'Unknown'}</div>
                      <div className="proj-cat">
                        <span className="num">{result.data.sliitId}</span>
                        {result.data.boardPosition ? ` · ${result.data.boardPosition}` : ''}
                        {' · '}
                        {result.data.table === 'board' ? 'Board Member' : 'Member'}
                      </div>
                    </div>
                    {!result.data.verified && (
                      <span className="pill pill-danger"><span className="dot" />Unverified</span>
                    )}
                    {result.data.notParticipating && (
                      <span className="pill pill-danger"><span className="dot" />Not Participating</span>
                    )}
                  </div>

                  {result.error && (
                    <div className="error-banner" style={{ marginTop: 'var(--sp-3)' }}>{result.error}</div>
                  )}

                  {result.data.verified && result.data.notParticipating && !result.data.alreadyChecked && !result.error && (
                    <div className="error-banner" style={{ marginTop: 'var(--sp-3)' }}>
                      <FaTimesCircle style={{ marginRight: 8 }} />
                      Registered as NOT participating. QR check-in is not allowed -- please check them in manually from the Installation list.
                    </div>
                  )}

                  {result.data.verified && (!result.data.notParticipating || result.data.alreadyChecked) && (
                    <div style={{ marginTop: 'var(--sp-4)' }}>
                      {result.data.alreadyChecked ? (
                        <div className="success-banner" style={{ justifyContent: 'center' }}>
                          <FaCheckCircle />
                          <span>
                            {result.justCheckedIn ? 'Checked in just now.' : 'Already checked in.'}
                          </span>
                        </div>
                      ) : (
                        <button
                          className="btn btn-primary"
                          style={{ width: '100%', justifyContent: 'center', minHeight: 48 }}
                          onClick={handleConfirmCheckIn}
                          disabled={confirming}
                        >
                          {confirming && <span className="spinner" style={{ marginRight: 8 }} />}
                          {confirming ? 'Checking in…' : 'Check-in'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              <button
                className="btn btn-ghost"
                style={{ width: '100%', justifyContent: 'center', marginTop: 'var(--sp-3)', minHeight: 44 }}
                onClick={scanNext}
              >
                Scan Next
              </button>
            </div>
          )}
        </div>
      </main>
    </>
  );
}
